import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { savePosProduct, deletePosProduct } from '../utils/posUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { getCurrencySymbol } from '../utils/currencyUtil';
import '../css/Pos.css';

/**
 * Register or edit one product.
 *
 * @param {object} props
 * @param {object|null} props.product - null to register a new one
 * @param {string[]} props.categories
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosProductModal({ product, categories, onClose }) {
  const { config } = useConfig();
  const [name, setName] = useState(product?.name || '');
  const [description, setDescription] = useState(product?.description || '');
  const [category, setCategory] = useState(product?.category || '');
  const [price, setPrice] = useState(product?.price ?? '');
  const [supplies, setSupplies] = useState(product?.supplies ?? '');
  const [imageFile, setImageFile] = useState(null);
  // Shows the newly chosen file before it is saved; falls back to the stored
  // image so editing a product doesn't look like it lost its picture.
  const [preview, setPreview] = useState(product?.imageUrl || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handlePickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImageFile(file);
    // Object URL rather than a FileReader: nothing needs the bytes here, only
    // something to point an <img> at until save compresses it properly.
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await savePosProduct(product, { name, description, category, price, supplies, imageFile });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deletePosProduct(product);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return createPortal(
    // No backdrop click-to-close: this form holds unsaved work
    // (CLAUDE.md > Behavior). The × and Cancel are the ways out.
    <div className="pos-modal-overlay">
      <div className="pos-modal" role="dialog" aria-modal="true" aria-label={product ? 'Edit product' : 'Add new product'}>
        <header className="pos-modal-head">
          <h3>{product ? 'Edit product' : 'Add new product'}</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <div className="pos-image-row">
            <div className="pos-image-preview">
              {preview ? <img src={preview} alt="" /> : <span>No image</span>}
            </div>
            <label className="pos-btn pos-btn-ghost pos-image-pick">
              {preview ? 'Replace image' : 'Choose image'}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handlePickImage}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          <label className="pos-field">
            <span>Product name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="pos-field">
            <span>Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Uncategorised</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
              {/* A product edited after its category was removed keeps that
                  category, so it stays selectable here rather than silently
                  resetting to Uncategorised on the next save. */}
              {category && !categories.includes(category) && (
                <option value={category}>{category} (removed)</option>
              )}
            </select>
          </label>

          <label className="pos-field">
            <span>Description</span>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>

          <div className="pos-field-row">
            <label className="pos-field">
              <span>Price ({getCurrencySymbol(config)})</span>
              <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
            </label>
            <label className="pos-field">
              <span>Available supplies</span>
              <input type="number" min="0" step="1" value={supplies} onChange={(e) => setSupplies(e.target.value)} />
            </label>
          </div>

          {error && <p className="pos-error">{error}</p>}
          {saving && <div className="pos-modal-saving">{spinnerRing('Saving…')}</div>}
        </div>

        <footer className="pos-modal-foot">
          {product && !confirmingDelete && (
            <button type="button" className="pos-btn pos-btn-danger" onClick={() => setConfirmingDelete(true)} disabled={saving}>
              Delete
            </button>
          )}
          {product && confirmingDelete && (
            <div className="pos-delete-confirm">
              <span>Delete this product? Past sales keep their own record of it.</span>
              <button type="button" className="pos-btn pos-btn-danger" onClick={handleDelete} disabled={saving}>Yes, delete</button>
              <button type="button" className="pos-btn pos-btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={saving}>Keep</button>
            </div>
          )}
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (product ? 'Save changes' : 'Register product')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosProductModal;
